import { ArrowDown, ArrowUp, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Group, SaveBar, getPath } from "@/admin/fields";
import { buildFaqGroups } from "@/content/faqData";

// Editor for every FAQ group on the site. Each group shows the currently
// effective questions and answers: the admin-saved list when one exists,
// otherwise the auto-generated defaults. Editing, adding, removing or
// reordering writes the full list back to content.faqs[key]. "Reset to
// auto-generated" clears the override so the page falls back to the smart
// defaults that stay in sync with the rest of the content.

const testable = (key) => key.replace(/[^a-z0-9]+/gi, "-");

const FaqTab = ({ content, update, saving, dirty, message, onSave }) => {
  const groups = buildFaqGroups(content);

  // Preserve visual sections (static pages first, then services grouped by
  // umbrella, then blog posts, then legal).
  let lastSection = null;

  return (
    <div data-testid="admin-tab-content-faqs">
      <SaveBar tabId="faqs" saving={saving} dirty={dirty} message={message} onSave={onSave} />
      <p className="af-hint-block">
        Every public page has a Frequently Asked Questions section. Edit any answer below and it
        updates both the on-page accordion and the structured data that Google and AI assistants read.
        Pages left on &lsquo;Auto&rsquo; generate their questions from the rest of your content, so they stay
        current on their own.
      </p>

      {groups.map((groupDef) => {
        const path = ["faqs", groupDef.key];
        const saved = getPath(content, path);
        const isCustom = Array.isArray(saved) && saved.length > 0;
        const items = isCustom ? saved : groupDef.auto;
        const tid = testable(groupDef.key);

        const writeItems = (next) => update(path, next);

        const setField = (index, field, value) =>
          writeItems(items.map((item, i) => (i === index ? { ...item, [field]: value } : { ...item })));

        const addItem = () => writeItems([...items.map((it) => ({ ...it })), { q: "", a: "" }]);

        const removeItem = (index) => {
          if (window.confirm("Remove this question?")) {
            writeItems(items.filter((item, i) => i !== index).map((it) => ({ ...it })));
          }
        };

        const move = (index, direction) => {
          const target = index + direction;
          if (target < 0 || target >= items.length) return;
          const next = items.map((it) => ({ ...it }));
          [next[index], next[target]] = [next[target], next[index]];
          writeItems(next);
        };

        const resetAuto = () => {
          if (window.confirm("Discard your custom questions for this page and use the auto-generated ones again?")) {
            writeItems([]);
          }
        };

        const sectionHeader =
          groupDef.section && groupDef.section !== lastSection ? groupDef.section : null;
        lastSection = groupDef.section || lastSection;

        return (
          <div key={groupDef.key}>
            {sectionHeader && <h3 className="af-section-heading">{sectionHeader}</h3>}
            <Group title={`${groupDef.label}  (${isCustom ? "Custom" : "Auto"})`}>
              <div className="af-faq-toolbar">
                <span className={isCustom ? "af-faq-badge af-faq-badge-custom" : "af-faq-badge"}>
                  {isCustom ? "Custom answers" : "Auto-generated"}
                </span>
                {isCustom && (
                  <button
                    type="button"
                    className="af-btn af-btn-ghost"
                    onClick={resetAuto}
                    data-testid={`admin-faq-reset-${tid}`}
                  >
                    <RotateCcw size={12} />
                    Reset to auto-generated
                  </button>
                )}
              </div>

              {items.map((item, index) => (
                <div className="af-faq-item" key={index} data-testid={`admin-faq-${tid}-item-${index}`}>
                  <div className="af-faq-item-head">
                    <span className="af-faq-num">Q{index + 1}</span>
                    <div className="af-faq-move">
                      <button
                        type="button"
                        className="af-icon-btn"
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                        aria-label="Move up"
                        data-testid={`admin-faq-${tid}-up-${index}`}
                      >
                        <ArrowUp size={13} />
                      </button>
                      <button
                        type="button"
                        className="af-icon-btn"
                        disabled={index === items.length - 1}
                        onClick={() => move(index, 1)}
                        aria-label="Move down"
                        data-testid={`admin-faq-${tid}-down-${index}`}
                      >
                        <ArrowDown size={13} />
                      </button>
                      <button
                        type="button"
                        className="af-icon-btn af-icon-btn-danger"
                        onClick={() => removeItem(index)}
                        aria-label="Remove"
                        data-testid={`admin-faq-${tid}-remove-${index}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  <input
                    className="af-input"
                    type="text"
                    placeholder="Question"
                    value={item.q || ""}
                    onChange={(e) => setField(index, "q", e.target.value)}
                    data-testid={`admin-faq-${tid}-q-${index}`}
                  />
                  <textarea
                    className="af-input af-textarea"
                    rows={3}
                    placeholder="Answer"
                    value={item.a || ""}
                    onChange={(e) => setField(index, "a", e.target.value)}
                    data-testid={`admin-faq-${tid}-a-${index}`}
                  />
                </div>
              ))}

              <button
                type="button"
                className="af-btn af-btn-primary"
                onClick={addItem}
                data-testid={`admin-faq-add-${tid}`}
              >
                <Plus size={13} />
                Add question
              </button>
            </Group>
          </div>
        );
      })}
    </div>
  );
};

export default FaqTab;
